<template>
  <div class="sk-signup-page">
    <div v-if="showSignedInBanner" class="sk-signed-in-banner">
      You're signed in as <strong>{{ $root.userEmail }}</strong>.
      <button type="button" class="sk-signed-in-logout" @click="logoutSignedIn">Log out</button>
      to create a different account.
    </div>

    <div class="sk-topbar sk-topbar-split">
      <a href="#" class="sk-logo" @click.prevent>
        <img src="https://sentrykit.com/wp-content/uploads/2026/04/Sk-2.0-logo-white.png" alt="SentryKit" style="height:48px;width:auto;display:block;">
      </a>
      <router-link to="/login" class="sk-topbar-login">Already a customer? <b>Log in</b></router-link>
    </div>

    <div class="sk-steps-bar">
      <div class="sk-step sk-step-active">
        <span class="sk-step-num">1</span>
        Create account <small class="sk-step-time">· ~1 min</small>
      </div>
      <div class="sk-step-connector"></div>
      <div class="sk-step" :class="{ 'sk-step-done': finishMode }">
        <span class="sk-step-num">{{ finishMode ? '✓' : '2' }}</span>
        Connect Amazon <small class="sk-step-time">· ~1 min</small>
      </div>
    </div>

    <div class="sk-main-wide">
      <div class="sk-two-col-card">

        <!-- Trust Panel -->
        <div class="sk-trust-panel" :class="{ 'sk-trust-panel-asgtg': isAsgtg, 'sk-story': !isAsgtg }">
          <template v-if="isAsgtg">
            <span class="sk-panel-badge">ASGTG Partner Offer</span>
            <div class="sk-asgtg-rating">
              <span class="sk-asgtg-stars">★★★★★</span>
              <b>4.8/5</b> · <span class="sk-asgtg-trust">Trusted by 3,000+ Amazon brands</span>
            </div>
            <h2 class="sk-panel-h sk-asgtg-h">Every signal on your Amazon listings — Buy Box, fees, suppression, stock, rank and more.</h2>
            <p class="sk-panel-sub">Built for sellers who found SentryKit through ASGTG. Real-time alerts the moment something changes, so problems get caught before they compound.</p>
            <p class="sk-asgtg-signals-lead">50+ signals across nine groups — every one of them on every plan.</p>
            <div class="sk-asgtg-grid">
              <div class="sk-asgtg-cell"><span class="n">01</span>Fees &amp; margin</div>
              <div class="sk-asgtg-cell"><span class="n">02</span>Buy Box &amp; pricing</div>
              <div class="sk-asgtg-cell"><span class="n">03</span>Account health &amp; policy</div>
              <div class="sk-asgtg-cell"><span class="n">04</span>Listing health &amp; suppression</div>
              <div class="sk-asgtg-cell"><span class="n">05</span>Product content &amp; fields</div>
              <div class="sk-asgtg-cell"><span class="n">06</span>Reviews &amp; feedback</div>
              <div class="sk-asgtg-cell"><span class="n">07</span>Rank &amp; visibility</div>
              <div class="sk-asgtg-cell"><span class="n">08</span>Inventory &amp; fulfilment</div>
              <div class="sk-asgtg-cell"><span class="n">09</span>Competitors &amp; hijackers</div>
            </div>
            <div class="sk-panel-reviews">
              <div class="sk-asgtg-review">
                <div class="sk-asgtg-stars sk-asgtg-quote-stars">★★★★★</div>
                <p class="sk-review-text">"Reliable and powerful. The alerts land immediately — it catches everything across our catalogue without us having to check anything manually."</p>
                <span class="sk-review-who">Maximilian A. · Founder, Small Business · G2</span>
              </div>
              <div class="sk-asgtg-review">
                <div class="sk-asgtg-stars sk-asgtg-quote-stars">★★★★★</div>
                <p class="sk-review-text">"The timeliness of notifications and the detail of 'before/after' changes. Excellent customer support."</p>
                <span class="sk-review-who">Verified User · Small Business · G2</span>
              </div>
            </div>
          </template>
          <template v-else>
            <div>
              <span class="sk-eyebrow">Amazon listing &amp; account monitoring</span>
              <h2 class="sk-story-h">See every change to your Amazon listings the moment it happens.</h2>
              <p class="sk-story-sub">Buy Box, hijackers, suppressions, fees, title and image edits. Sent to Email, Slack, Teams or ClickUp, wherever your team already works.</p>
            </div>

            <!-- Example alerts: illustrative, match wording to real alert templates before launch -->
            <div class="sk-feed" aria-label="Example alerts">
              <div class="sk-feed-head"><span>What your alerts look like</span><span class="sk-live"><i></i>Monitoring</span></div>

              <div class="sk-alert">
                <span class="sk-alert-ic sk-alert-ic-bb" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 7h16l-1.5 11a2 2 0 0 1-2 1.7H7.5a2 2 0 0 1-2-1.7L4 7z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M9 7a3 3 0 0 1 6 0" stroke="currentColor" stroke-width="2"/></svg></span>
                <div><div class="sk-alert-t">Buy Box lost</div><div class="sk-alert-d">An unknown seller took it at <b>$24.99</b>. Your offer: $26.49.</div></div>
                <div class="sk-alert-m"><span class="sk-alert-ch">Slack</span><br>just now</div>
              </div>

              <div class="sk-alert">
                <span class="sk-alert-ic sk-alert-ic-tx" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 20h4L19 9l-4-4L4 16v4z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg></span>
                <div><div class="sk-alert-t">Title changed</div><div class="sk-alert-d"><s>Turmeric Curcumin Capsules, 120 Count</s><br><b>Turmeric Capsules, 60 Count</b></div></div>
                <div class="sk-alert-m"><span class="sk-alert-ch">Email</span><br>4 min ago</div>
              </div>

              <div class="sk-alert sk-alert-dim">
                <span class="sk-alert-ic sk-alert-ic-fee" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 3v18M16.5 7.5c0-1.7-2-3-4.5-3s-4.5 1.3-4.5 3S9.5 10.4 12 11s4.5 1.6 4.5 3.5-2 3-4.5 3-4.5-1.3-4.5-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>
                <div><div class="sk-alert-t">FBA fee increased</div><div class="sk-alert-d">+$0.61 per unit. Amazon moved this item to a new size tier.</div></div>
                <div class="sk-alert-m"><span class="sk-alert-ch">Teams</span><br>1 h ago</div>
              </div>

              <div class="sk-feed-foot">Before and after on every change, across <b>50+ signals</b> per listing.</div>
            </div>

            <!-- Customer voices: quotes are verbatim from customer emails; don't edit them -->
            <section class="sk-voices" aria-labelledby="sk-voices-h" @mouseenter="pauseVoices" @mouseleave="resumeVoices" @focusin="pauseVoices" @focusout="resumeVoices">
              <div class="sk-voices-head">
                <h3 id="sk-voices-h">What sellers tell us</h3>
                <div class="sk-voices-tabs" role="tablist" aria-label="What sellers mention">
                  <button v-for="(v, k) in voices" :key="v.tab" type="button" role="tab" :aria-selected="k === voiceIndex ? 'true' : 'false'" @click="selectVoice(k)">{{ v.tab }}</button>
                </div>
              </div>
              <div class="sk-voices-stage">
                <figure v-for="(v, k) in voices" :key="v.tab" class="sk-voice" :class="{ on: k === voiceIndex }" role="tabpanel">
                  <p v-if="v.ours" class="sk-voice-promise">{{ v.text }}</p>
                  <blockquote v-else>"{{ v.text }}"</blockquote>
                  <figcaption :class="{ ours: v.ours }">{{ v.who }}</figcaption>
                </figure>
              </div>
              <div class="sk-voices-bar" aria-hidden="true"><i :key="voiceCycle" :class="{ run: !voicePaused && !reduceMotion }"></i></div>
              <p class="sk-voices-foot">From customer emails to our team. Sellers across the US, UK, Italy, Germany, Austria and beyond. <a href="https://www.g2.com/products/sentrykit/reviews" target="_blank" rel="noopener">Public reviews on G2 →</a></p>
            </section>
          </template>
        </div>

        <!-- Form Panel -->
        <div class="sk-form-panel">
          <template v-if="finishMode">
            <div class="sk-amz-connected"><span class="sk-micro-tick">✓</span> Amazon connected<template v-if="pendingAmazon">: {{ pendingAmazon.marketplaceCount }} {{ pendingAmazon.marketplaceCount === 1 ? 'marketplace' : 'marketplaces' }} in {{ regionLabel(pendingAmazon.region) }}</template></div>
            <h1>Finish setting up</h1>
            <p class="sk-form-sub">Add your email, name and password.</p>
          </template>
          <h1 v-else>Start your 14-day free trial</h1>
          <div v-if="!finishMode" class="sk-offer">
            <span><span class="sk-micro-tick">✓</span> No credit card</span>
            <span><span class="sk-micro-tick">✓</span> Full access to every alert</span>
            <span><span class="sk-micro-tick">✓</span> Cancel anytime</span>
          </div>
          <div v-if="!finishMode" class="sk-perks">
            <div class="sk-perk">
              <span class="sk-perk-ic" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.6-3.4 3.3-5.5 6.5-5.5s5.9 2.1 6.5 5.5"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 14.8c1.6.8 2.7 2.6 3 5.2"/></svg></span>
              <div><div class="sk-perk-t">Dedicated onboarding</div><div class="sk-perk-d">For every account, every plan</div></div>
            </div>
            <div class="sk-perk">
              <span class="sk-perk-ic" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a8.5 8.5 0 0 1-12.6 7.4L3 21l1.6-5.4A8.5 8.5 0 1 1 21 12z"/><path d="M8.5 12h.01M12 12h.01M15.5 12h.01"/></svg></span>
              <div><div class="sk-perk-t">Immediate support</div><div class="sk-perk-d">Real people, anytime you need us</div></div>
            </div>
          </div>

          <div v-if="isAsgtg" class="sk-via-asgtg">
            <span>✓</span> Signing up via <b>ASGTG</b> — your member offer is already applied.
          </div>

          <div v-if="error" class="sk-error-msg" role="alert">
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke-width="2"/><path stroke-width="2" d="M12 8v4m0 4h.01"/></svg>
            {{ error }}
          </div>

          <div v-if="showSso" class="sk-sso">
            <span class="sk-field-label">Sign up with</span>
            <div class="sk-sso-row">
              <button v-if="googleSignupEnabled" type="button" class="sk-sso-google" aria-label="Sign up with Google" @click="startGoogleSignup">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
                Google
              </button>
              <button v-if="showAmazon" type="button" class="sk-sso-amz" :class="{ loading: amazonWorking }" :aria-busy="amazonWorking ? 'true' : 'false'" aria-label="Sign up with Amazon" @click="startAmazonSignup">
                <span v-if="amazonWorking" class="sk-spinner"></span>
                <img v-else :src="amazonWordmark" alt="Amazon" class="sk-amz-logo">
              </button>
            </div>
            <div v-if="showAmazon" class="sk-region-pick">
              <button v-if="!showRegionPicker" type="button" class="sk-link-btn" @click="showRegionPicker = true">Selling outside North America?</button>
              <label v-else class="sk-region-select">
                <span>Amazon region</span>
                <select v-model="amazonRegion" class="sk-select">
                  <option v-for="r in amazonRegions" :key="r.value" :value="r.value">{{ r.label }}</option>
                </select>
              </label>
            </div>
            <div class="sk-or">or sign up with email</div>
          </div>

          <form novalidate autocomplete="on" @submit.prevent="signup" @focusin="onSignupFormStarted">
            <div class="field" :class="{ 'has-error': fieldErrors.email }">
              <label for="signup-email">Work email</label>
              <input id="signup-email" ref="email" class="sk-input" :class="{ error: fieldErrors.email }" type="email" name="email" inputmode="email" autocomplete="email" autocapitalize="off" spellcheck="false" v-model="email" placeholder="you@yourbrand.com" :aria-invalid="fieldErrors.email ? 'true' : 'false'" @blur="onBlur('email')" @input="onEmailInput">
              <div v-if="fieldErrors.email" class="sk-field-error">Enter a valid email address, like name@company.com.</div>
            </div>

            <div class="field" :class="{ 'has-error': fieldErrors.name }">
              <label for="signup-name">Your name</label>
              <input id="signup-name" ref="name" class="sk-input" :class="{ error: fieldErrors.name }" type="text" name="name" autocomplete="name" v-model="fullName" placeholder="First and last name" :aria-invalid="fieldErrors.name ? 'true' : 'false'" @blur="onBlur('name')" @input="onInput('name')">
              <div v-if="fieldErrors.name" class="sk-field-error">Tell us what to call you.</div>
            </div>

            <div class="field" :class="{ 'has-error': fieldErrors.password }">
              <label for="signup-password">Password</label>
              <div class="sk-password-wrap">
                <input id="signup-password" ref="password" class="sk-input" :class="{ error: fieldErrors.password }" :type="showPassword ? 'text' : 'password'" name="password" autocomplete="new-password" v-model="password" placeholder="At least 8 characters" aria-describedby="signup-pw-hint" :aria-invalid="fieldErrors.password ? 'true' : 'false'" @blur="onBlur('password')" @input="onInput('password')">
                <button type="button" class="sk-toggle-pw" :aria-label="showPassword ? 'Hide password' : 'Show password'" :aria-pressed="showPassword ? 'true' : 'false'" @click="showPassword = !showPassword">
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                </button>
              </div>
              <div v-if="fieldErrors.password" class="sk-field-error">Use 8+ characters with a letter and a number or symbol.</div>
              <div v-else id="signup-pw-hint" class="sk-pw-hint" :class="{ met: password && !passwordWeak }">
                <span>{{ password && !passwordWeak ? '✓' : '○' }}</span> At least 8 characters
              </div>
            </div>

            <div v-if="!finishMode" class="field" :class="{ 'has-error': fieldErrors.accountType }">
              <span class="sk-field-label" id="signup-seller-type-label">What kind of seller are you?</span>
              <div class="sk-seg" role="radiogroup" aria-labelledby="signup-seller-type-label">
                <template v-for="(opt, k) in sellerTypes">
                  <input :key="opt.value + '-input'" :id="'signup-st-' + opt.value" :ref="k === 0 ? 'accountType' : null" type="radio" name="accountType" :value="opt.value" v-model="accountType" @change="onInput('accountType', true)">
                  <label :key="opt.value + '-label'" :for="'signup-st-' + opt.value">{{ opt.label }}</label>
                </template>
              </div>
              <div v-if="fieldErrors.accountType" class="sk-field-error">Pick the one closest to you. It tailors your alerts.</div>
            </div>

            <div v-if="!finishMode" class="field">
              <label for="signup-company">Company <span class="sk-opt">Optional</span></label>
              <input id="signup-company" class="sk-input" type="text" name="organization" autocomplete="organization" v-model="companyName" placeholder="e.g. Acme Brands LLC">
            </div>

            <button type="submit" class="sk-cta-btn" :class="{ loading: working }" :aria-busy="working ? 'true' : 'false'">
              <span v-if="working" class="sk-spinner"></span>
              <span v-else-if="finishMode">Finish &amp; start syncing →</span>
              <span v-else>Create account &amp; continue →</span>
            </button>
            <!-- Pending legal OK: replaces the terms checkbox -->
            <p class="sk-legal">By creating an account you agree to SentryKit's <a href="https://www.sentrykit.com/terms" target="_blank">Terms of Service</a> and <a href="https://www.sentrykit.com/privacy" target="_blank">Privacy Policy</a>.</p>
          </form>

          <div v-if="!finishMode" class="sk-next-note">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5l8-3z" stroke="#1E9E6A" stroke-width="2" stroke-linejoin="round"/><path d="M8.5 12l2.5 2.5 4.5-5" stroke="#1E9E6A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span><b>Next: connect Amazon.</b> You approve access on Amazon's own Seller Central screen, so we never see your password. SentryKit doesn't edit your listings, prices or inventory.</span>
          </div>

          <div v-if="chatAvailable" class="sk-form-footer">
            Questions first? <a href="#" @click.prevent="openChat">Chat with us</a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import './styles/signup-flow.css'
import { pushGtmEvent, markConnectStoreStarted, fireConnectStoreSuccess } from './utils/gtmEvents'
import { isAsgtgSignupPath } from './utils/publicAuthRoutes'
import { amazonAuthorizationQueryMarketplace, regionMarketplaceCodes } from './utils/amazonSellingPartnerOAuth'
import { GOOGLE_SSO_ERROR_MESSAGES, googleReturnPath, startGoogleSignIn } from './utils/googleSignIn'
import amazonWordmark from './assets/amazon-wordmark.png'

var ASGTG_SOURCE = 'asgtg'
var ASGTG_PROMO_CODE = 'ASGTG20'
var VOICE_INTERVAL_MS = 6500
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
var FIELD_ORDER = ['email', 'name', 'password', 'accountType']
var FIELD_LABELS = { email: 'work email', name: 'your name', password: 'password', accountType: 'seller type' }
// Same regions as AmazonRegionConnect. One region per Amazon approval; more can be added in Settings → Sellers.
var AMAZON_REGIONS = [
  { value: 'NA', label: 'North America' },
  { value: 'EU', label: 'Europe' },
  { value: 'FE', label: 'Asia Pacific' },
  { value: 'ME', label: 'Middle East' },
  { value: 'IN', label: 'India' }
]
var AMAZON_EXPIRED_MESSAGE = 'That Amazon approval expired. Click Sign up with Amazon again. It takes a few seconds.'
var SSO_ERROR_MESSAGES = Object.assign({}, GOOGLE_SSO_ERROR_MESSAGES, {
  amazon_cancelled: 'Amazon sign-up was cancelled. You can try again or sign up with email.',
  amazon_exchange: "We couldn't finish connecting to Amazon. Please click Sign up with Amazon again.",
  amazon_already_connected: 'This Amazon seller account is already connected to SentryKit. Log in with that account, or contact support@sentrykit.com.',
  amazon_legacy_account: 'This Amazon seller account is registered with legacy SentryKit. Log in with your existing email, or contact support@sentrykit.com to migrate.',
  amazon_failed: 'Something went wrong connecting to Amazon. Please try again or sign up with email.'
})

export default {
  data: function () {
    var emailhash = this.$root.deeplink ? this.$root.deeplink.indexOf('email=') : -1
    var initialEmail = (emailhash > -1) ? this.$root.deeplink.substr(emailhash + 6) : ''
    return {
      companyName: '',
      accountType: '',
      fullName: '',
      email: initialEmail,
      typedEmail: initialEmail,
      password: '',
      showPassword: false,
      fieldErrors: { email: false, name: false, password: false, accountType: false },
      error: '',
      working: false,
      invite: '',
      signupFormStartedTracked: false,
      chatAvailable: false,
      sellerTypes: [
        { value: 'brand', label: 'Private label / brand' },
        { value: 'merchant', label: 'Wholesale / distribution' },
        { value: 'agency', label: 'Agency / aggregator' },
        { value: 'oa', label: 'Online arbitrage' }
      ],
      voices: [
        { tab: 'Speed', text: 'The notification arrived even before I saw it in Seller Central, so perfect!', who: 'Seller · Italy' },
        { tab: 'Support', text: "I'm really happy with it and the support is excellent.", who: 'Seller · Italy' },
        { tab: 'Detail', text: 'Very informative and helpful. … A+ product so far!', who: 'Supplement brand · United States' },
        { tab: 'Value', ours: true, text: 'Many tools charge per ASIN, so every new marketplace raises your bill. We charge one flat price per storefront: unlimited ASINs, every marketplace.', who: 'Our pricing promise' },
        { tab: 'Teams', text: 'The trial went quite well. … I would like to show this to the MDs of the company world wide', who: 'Electronics brand · United Kingdom' }
      ],
      voiceIndex: 0,
      voiceCycle: 0,
      voicePaused: false,
      voiceTimer: null,
      reduceMotion: false,
      amazonWordmark: amazonWordmark,
      amazonRegions: AMAZON_REGIONS,
      amazonRegion: 'NA',
      showRegionPicker: false,
      amazonWorking: false,
      googleSignupEnabled: true,
      finishMode: this.$route.query.finish === 'amazon',
      pendingAmazon: null
    }
  },
  computed: {
    showSignedInBanner: function () {
      return !!(this.$root.authCompleted && this.$root.userEmail)
    },
    isAsgtg: function () {
      return isAsgtgSignupPath(this.$route.path)
    },
    showSso: function () {
      return !this.showSignedInBanner && !this.finishMode
    },
    // Appstore arrivals (amazon_callback_uri) are already mid-authorization with Amazon. Google stays:
    // its return path keeps those params, so the existing handoff continues after sign-in.
    showAmazon: function () {
      return !this.$route.query.amazon_callback_uri
    },
    signupMethod: function () {
      return this.finishMode ? 'amazon' : 'email'
    },
    passwordWeak: function () {
      var regex1 = /.*[_\- !"?$%^&*()+={}[\]:;@'~#|<>,.?\\/0123456789].*/
      var regex2 = /.*[a-zA-Z].*/
      return this.password.length < 8 || !regex1.test(this.password) || !regex2.test(this.password)
    },
    // First word → firstName, the rest → lastName, so the signup payload is unchanged.
    nameParts: function () {
      var parts = this.fullName.trim().split(/\s+/)
      return { first: parts[0] || '', last: parts.slice(1).join(' ') }
    }
  },
  methods: {
    logoutSignedIn: function () {
      if (this.$root.signOutUser) this.$root.signOutUser()
    },
    onSignupFormStarted: function () {
      if (this.signupFormStartedTracked) return
      this.signupFormStartedTracked = true
      pushGtmEvent({ event: 'sign_up_form_started', step: 1, method: this.signupMethod })
    },
    fieldValid: function (key) {
      switch (key) {
        case 'email': return EMAIL_REGEX.test(this.email.trim())
        case 'name': return this.nameParts.first !== ''
        case 'password': return !this.passwordWeak
        case 'accountType': return this.accountType !== ''
      }
      return true
    },
    checkField: function (key) {
      var ok = this.fieldValid(key)
      this.fieldErrors[key] = !ok
      return ok
    },
    onBlur: function (key) {
      var value = { email: this.email, name: this.fullName, password: this.password }[key]
      if (value) this.checkField(key)
    },
    // Re-check while typing only once a field has been flagged, so errors clear as soon as they're fixed.
    onInput: function (key, always) {
      if (always || this.fieldErrors[key]) this.checkField(key)
    },
    // Picking a saved login from the password field's autofill dropdown also overwrites the email
    // with that account's username. Only accept email changes made while the email field is focused.
    onEmailInput: function (e) {
      if (document.activeElement === e.target || !this.typedEmail) {
        this.typedEmail = this.email
      } else if (this.email !== this.typedEmail) {
        this.email = this.typedEmail
      }
      this.onInput('email')
    },
    validateAll: function () {
      var order = this.finishMode ? ['email', 'name', 'password'] : FIELD_ORDER
      return order.filter((key) => !this.checkField(key))
    },
    missingFieldsMessage: function (bad) {
      var labels = bad.map(function (key) { return FIELD_LABELS[key] })
      var list = labels.length > 1 ? labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1] : labels[0]
      return 'Almost there. Please add your ' + list + '.'
    },
    focusField: function (key) {
      var el = this.$refs[key]
      if (Array.isArray(el)) el = el[0]
      if (el && el.focus) el.focus()
    },
    getRecaptchaToken: function () {
      return new Promise(function (resolve) {
        var siteKey = window.RECAPTCHA_SITE_KEY
        if (!siteKey || typeof window.grecaptcha === 'undefined') {
          resolve(null)
          return
        }
        window.grecaptcha.ready(function () {
          window.grecaptcha.execute(siteKey, { action: 'signup' }).then(resolve, function () { resolve(null) })
        })
      })
    },
    signup: function () {
      if (this.working) return
      var bad = this.validateAll()
      if (bad.length) {
        this.error = this.missingFieldsMessage(bad)
        this.focusField(bad[0])
        return
      }
      this.working = true
      this.error = ''
      this.getRecaptchaToken().then((recaptchaToken) => {
        var timezoneOffsetMins = 0 - new Date().getTimezoneOffset()
        var data = {
          email: this.email.trim(),
          firstName: this.nameParts.first,
          companyName: this.companyName.trim(),
          accountType: this.accountType,
          lastName: this.nameParts.last,
          password: this.password,
          inviteCode: this.invite,
          timezoneOffsetMins: timezoneOffsetMins,
          recaptchaToken: recaptchaToken
        }
        if (this.isAsgtg) {
          data.source = ASGTG_SOURCE
          data.inviteCode = ASGTG_PROMO_CODE
        }
        // Amazon finish takes the same payload; the backend reads the pending Amazon signup from a cookie.
        var endpoint = this.finishMode ? '/api/auth/amazon/finish' : '/api/auth/signup/standard'
        this.$http.post(endpoint, data).then(
          () => {
            try { window.fpr('referral', { email: data.email }) } catch (e) { /* noop */ }
            var created = {
              event: 'sign_up_account_created',
              step: 1,
              method: this.signupMethod
            }
            if (this.accountType) created.seller_type = this.accountType
            pushGtmEvent(created, () => {
              if (this.finishMode) {
                // Amazon sign-up connects inside this form, so connect_store_success has to fire here.
                // On /signup, checkIfAuthenticated() would not leave the page.
                fireConnectStoreSuccess('amazon', function () {
                  window.location.assign('/welcome')
                })
              } else {
                this.login(data)
              }
            })
          },
          (response) => {
            this.working = false
            var body = response.body || {}
            // 410 = pending signup expired; amazon_* 409 = seller connected elsewhere. Either way it's gone.
            if (this.finishMode && (response.status === 410 || String(body.code || '').indexOf('amazon_') === 0)) {
              this.finishMode = false
              this.pendingAmazon = null
            }
            // 422 from UserRegistrationService returns an array of messages.
            var msg = Array.isArray(body.message)
              ? body.message.map(function (m) { return String(m).replace(/\.?$/, '.') }).join(' ')
              : body.message
            msg = msg || 'Signup failed. Please try again.'
            this.error = msg
            pushGtmEvent({
              event: 'sign_up_form_error',
              error_field: body.field || 'general',
              error_message: msg
            })
          }
        )
      })
    },
    login: function (data) {
      this.$http.post('/api/auth/login/standard', {
        email: data.email,
        password: data.password,
        keepMeSignedIn: true
      }).then(
        () => {
          this.working = false
          setTimeout(() => {
            this.$root.pendingPostCredentialsRedirect = true
            this.$root.checkIfAuthenticated()
          }, 100)
        },
        (response) => {
          this.working = false
          this.error = response.body.message || 'Account created but login failed. Please try logging in.'
        }
      )
    },
    regionLabel: function (code) {
      var match = AMAZON_REGIONS.filter(function (r) { return r.value === code })[0]
      return match ? match.label : 'North America'
    },
    startGoogleSignup: function () {
      startGoogleSignIn('signup', googleReturnPath(this.$route), this.isAsgtg ? ASGTG_SOURCE : '')
    },
    startAmazonSignup: function () {
      if (this.amazonWorking) return
      this.amazonWorking = true
      this.error = ''
      var region = this.amazonRegion
      var location = null
      var gtmDone = false
      var go = function () {
        if (location && gtmDone) window.location = location
      }
      // Lets maybeFireConnectStoreSuccess() fire connect_store_success once the user lands on /welcome.
      markConnectStoreStarted()
      pushGtmEvent({ event: 'sso_click', provider: 'amazon' }, function () {
        gtmDone = true
        go()
      })
      this.$http.get('/api/auth/amazon/start', {
        params: {
          region: region,
          allMarketplaces: true,
          marketplace: amazonAuthorizationQueryMarketplace(region),
          marketplaceCodes: regionMarketplaceCodes(region).join(','),
          source: this.isAsgtg ? ASGTG_SOURCE : ''
        }
      }).then(
        (response) => {
          location = response.body.location
          go()
        },
        (response) => {
          this.amazonWorking = false
          this.error = (response.body && response.body.message) || "Couldn't start Amazon sign-up. Please try again."
          pushGtmEvent({ event: 'sso_error', provider: 'amazon', reason: 'start_failed' })
        }
      )
    },
    loadPendingAmazonSignup: function () {
      // One-time code from the Amazon callback; the backend trades it for the pending-signup cookie
      // on this host. Strip it from the address bar right away so it isn't kept in history.
      var handoff = this.$route.query.handoff
      if (handoff) {
        try {
          var url = new URL(window.location.href)
          url.searchParams.delete('handoff')
          window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
        } catch (e) { /* noop */ }
      }
      this.$http.get('/api/auth/amazon/pending', { params: handoff ? { handoff: handoff } : {} }).then(
        (response) => { this.pendingAmazon = response.body },
        (response) => {
          this.finishMode = false
          this.error = (response.body && response.body.message) || AMAZON_EXPIRED_MESSAGE
          pushGtmEvent({ event: 'sso_error', provider: 'amazon', reason: 'amazon_expired' })
        }
      )
    },
    showSsoError: function (reason) {
      var provider = reason.indexOf('amazon_') === 0 ? 'amazon' : 'google'
      this.error = SSO_ERROR_MESSAGES[reason] || SSO_ERROR_MESSAGES[provider + '_failed']
      pushGtmEvent({ event: 'sso_error', provider: provider, reason: reason })
    },
    openChat: function () {
      if (typeof window.Intercom === 'function') window.Intercom('show')
    },
    showVoice: function (n) {
      this.voiceIndex = (n + this.voices.length) % this.voices.length
      this.voiceCycle++
    },
    selectVoice: function (n) {
      this.showVoice(n)
      this.startVoices()
    },
    startVoices: function () {
      clearInterval(this.voiceTimer)
      if (this.reduceMotion || this.isAsgtg) return
      this.voiceTimer = setInterval(() => {
        if (!this.voicePaused) this.showVoice(this.voiceIndex + 1)
      }, VOICE_INTERVAL_MS)
    },
    pauseVoices: function () {
      this.voicePaused = true
    },
    resumeVoices: function () {
      this.voicePaused = false
      this.showVoice(this.voiceIndex)
      this.startVoices()
    }
  },
  created: function () {
    this.$root.$emit('fb-pixel-event', 'standard', 'Lead')
    if (this.finishMode) this.loadPendingAmazonSignup()
    if (this.$route.query.sso_error) this.showSsoError(String(this.$route.query.sso_error))
    if (this.isAsgtg) {
      this.invite = ASGTG_PROMO_CODE
      return
    }
    if (this.$route.query.code) {
      this.invite = this.$route.query.code
    }
    if (this.$route.query.ref) {
      this.$http.get('/api/identity/invite/firstPromoter', { params: { ref_id: this.$route.query.ref } }).then(
        (response) => { this.invite = response.body.promo_code },
        () => {}
      )
    }
  },
  mounted: function () {
    this.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    this.chatAvailable = typeof window.Intercom === 'function'
    // Back from Amazon's consent page restores this page from the bfcache with the button still spinning.
    this.onPageShow = (e) => { if (e.persisted) this.amazonWorking = false }
    window.addEventListener('pageshow', this.onPageShow)
    this.startVoices()
  },
  beforeDestroy: function () {
    clearInterval(this.voiceTimer)
    window.removeEventListener('pageshow', this.onPageShow)
  }
}
</script>

<style scoped>
.sk-signup-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Roboto', 'DM Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.sk-signed-in-banner {
  padding: 12px 24px;
  text-align: center;
  font-size: 14px;
  color: #1e293b;
  background: #fef3c7;
  border-bottom: 1px solid #fcd34d;
}
.sk-signed-in-banner strong { font-weight: 600; }
.sk-signed-in-logout {
  margin: 0 4px;
  padding: 0;
  border: none;
  background: none;
  color: #b45309;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
}
.sk-signed-in-logout:hover { color: #92400e; }

/* ── Header + stepper ── */
.sk-topbar-split { justify-content: space-between; padding: 0 24px; box-sizing: border-box; }
.sk-topbar-login { color: #cfe0f2; font-size: 14px; text-decoration: none; }
.sk-topbar-login b { color: #fff; font-weight: 600; }
.sk-topbar-login:hover b { text-decoration: underline; }
.sk-step-time { color: #8099B8; font-weight: 400; font-size: 12px; }

/* ── Left: story (non-ASGTG) ── */
.sk-story {
  position: relative;
  background: radial-gradient(120% 80% at 100% 0%, #0a6a96 0%, rgba(10,106,150,0) 55%), linear-gradient(165deg, #004D73 0%, #122E52 100%);
  color: #fff;
  padding: 44px 44px 40px;
  gap: 30px;
  overflow: hidden;
}
.sk-eyebrow {
  display: inline-block;
  font-size: 11.5px;
  letter-spacing: .09em;
  text-transform: uppercase;
  font-weight: 700;
  color: #ffd2a8;
  background: rgba(245,132,31,.16);
  border: 1px solid rgba(245,132,31,.35);
  padding: 6px 12px;
  border-radius: 999px;
}
.sk-story-h { font-family: Lato, Roboto, sans-serif; font-size: 30px; line-height: 1.2; font-weight: 900; margin: 14px 0 0; color: #fff; }
.sk-story-sub { color: #d4e3f1; margin: 12px 0 0; font-size: 15.5px; line-height: 1.5; }

.sk-feed { display: grid; gap: 10px; }
.sk-feed-head { display: flex; justify-content: space-between; align-items: center; font-size: 12px; letter-spacing: .06em; text-transform: uppercase; color: #9fc1de; font-weight: 700; margin-bottom: 2px; }
.sk-live { display: inline-flex; align-items: center; gap: 6px; text-transform: none; letter-spacing: 0; font-weight: 500; color: #bfe9d6; }
.sk-live i { width: 7px; height: 7px; border-radius: 50%; background: #3ddc97; animation: sk-pulse 2s infinite; }
@keyframes sk-pulse { 0% { box-shadow: 0 0 0 0 rgba(61,220,151,.55) } 70% { box-shadow: 0 0 0 8px rgba(61,220,151,0) } 100% { box-shadow: 0 0 0 0 rgba(61,220,151,0) } }
.sk-alert { display: grid; grid-template-columns: 34px minmax(0,1fr) auto; gap: 12px; align-items: start; background: #fff; color: #14253D; border-radius: 12px; padding: 12px 14px; box-shadow: 0 10px 26px rgba(4,20,40,.28); opacity: 0; transform: translateY(10px); animation: sk-in .5s ease forwards; }
.sk-alert:nth-child(2) { animation-delay: .15s } .sk-alert:nth-child(3) { animation-delay: .35s } .sk-alert:nth-child(4) { animation-delay: .55s }
.sk-alert-dim { animation-name: sk-indim; }
@keyframes sk-in { to { opacity: 1; transform: none } }
@keyframes sk-indim { to { opacity: .72; transform: scale(.98) } }
.sk-alert-ic { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; }
.sk-alert-ic-bb { background: #FFF1E4; color: #D2600A; } .sk-alert-ic-tx { background: #E8F0FD; color: #2069C8; } .sk-alert-ic-fee { background: #E7F7EF; color: #14855A; }
.sk-alert-t { font-weight: 700; font-size: 14px; line-height: 1.3; }
.sk-alert-d { font-size: 13px; color: #5B6B80; margin-top: 2px; line-height: 1.4; }
.sk-alert-d s { color: #9aa8b8; }
.sk-alert-d b { color: #14253D; font-weight: 500; }
.sk-alert-m { text-align: right; font-size: 11.5px; color: #8a99ab; white-space: nowrap; line-height: 1.5; }
.sk-alert-ch { display: inline-block; font-size: 11px; font-weight: 500; color: #34506f; background: #EEF3F9; border-radius: 6px; padding: 1px 6px; }
.sk-feed-foot { font-size: 13px; color: #a9c4dc; margin-top: 2px; }
.sk-feed-foot b { color: #fff; font-weight: 500; }

.sk-voices { border-top: 1px solid rgba(255,255,255,.14); padding-top: 24px; }
.sk-voices-head { display: flex; flex-direction: column; gap: 12px; }
.sk-voices-head h3 { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #9fc1de; font-weight: 700; margin: 0; }
.sk-voices-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.sk-voices-tabs button { font: 500 13px Roboto, sans-serif; color: #cfe0f2; background: transparent; border: 1px solid rgba(255,255,255,.18); border-radius: 999px; padding: 5px 12px; cursor: pointer; transition: all .2s; }
.sk-voices-tabs button:hover { border-color: rgba(255,255,255,.4); color: #fff; }
.sk-voices-tabs button[aria-selected="true"] { background: #fff; color: #122E52; border-color: #fff; }
.sk-voices-stage { position: relative; min-height: 118px; margin-top: 18px; }
.sk-voice { position: absolute; top: 0; left: 0; right: 0; margin: 0; opacity: 0; transform: translateY(6px); transition: opacity .45s ease, transform .45s ease; pointer-events: none; }
.sk-voice.on { opacity: 1; transform: none; pointer-events: auto; position: relative; }
.sk-voice blockquote { margin: 0; font: italic 400 20px/1.45 Lato, Georgia, serif; color: #f2f7fc; }
.sk-voice figcaption { margin-top: 10px; font-size: 13px; color: #ffc58f; font-weight: 500; letter-spacing: .01em; }
.sk-voice figcaption.ours { color: #9fc1de; }
.sk-voice-promise { margin: 0; font: 700 19px/1.45 Lato, sans-serif; color: #fff; }
.sk-voices-bar { height: 2px; background: rgba(255,255,255,.12); border-radius: 2px; margin-top: 16px; overflow: hidden; }
.sk-voices-bar i { display: block; height: 100%; width: 0; background: #F5841F; }
.sk-voices-bar i.run { animation: sk-fill 6.5s linear forwards; }
@keyframes sk-fill { to { width: 100% } }
.sk-voices-foot { margin: 14px 0 0; font-size: 12.5px; color: #a9c4dc; }
.sk-voices-foot a { color: #cfe2f5; white-space: nowrap; }

@media (prefers-reduced-motion: reduce) {
  .sk-alert { animation: none; opacity: 1; transform: none; }
  .sk-alert-dim { opacity: .72; }
  .sk-live i { animation: none; }
  .sk-voice { transition: none; }
  .sk-voices-bar { display: none; }
}
@media (max-width: 560px) {
  .sk-story { padding: 32px 22px; }
  .sk-step-time { display: none; }
}

/* ── Right: form ── */
.sk-offer { display: flex; flex-wrap: wrap; gap: 6px 16px; margin: 8px 0 22px; color: #4A6080; font-size: 14px; }
.sk-offer > span { display: inline-flex; align-items: center; gap: 6px; }
.sk-field-label { display: block; font-size: 12px; font-weight: 600; color: #4A6080; margin-bottom: 6px; letter-spacing: .1px; }
.sk-opt { font-weight: 400; color: #8099B8; margin-left: auto; font-size: 12px; }
label[for="signup-company"] { display: flex; align-items: baseline; }
.sk-perks { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: -8px 0 22px; }
.sk-perk { display: flex; gap: 10px; align-items: flex-start; padding: 12px; background: #F5F8FC; border: 1px solid rgba(18,46,82,0.12); border-radius: 10px; }
.sk-perk-ic { flex: none; width: 28px; height: 28px; border-radius: 8px; display: grid; place-items: center; background: #FFF1E4; color: #D2600A; }
.sk-perk-t { font-size: 13px; font-weight: 700; color: #0d1f35; line-height: 1.3; }
.sk-perk-d { font-size: 12px; color: #4A6080; margin-top: 2px; line-height: 1.35; }
@media (max-width: 420px) { .sk-perks { grid-template-columns: 1fr; } .sk-sso-row { grid-auto-flow: row; } }
.sk-pw-hint { font-size: 12.5px; color: #8099B8; margin-top: 6px; display: flex; align-items: center; gap: 6px; }
.sk-pw-hint.met { color: #16A34A; }
.sk-seg { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.sk-seg input { position: absolute; opacity: 0; pointer-events: none; }
.sk-form-panel .sk-seg label { display: flex; align-items: center; justify-content: center; text-align: center; min-height: 42px; padding: 6px 10px; border: 1.5px solid rgba(18,46,82,0.22); border-radius: 10px; font-size: 14px; font-weight: 400; color: #0d1f35; cursor: pointer; margin: 0; letter-spacing: 0; transition: all .15s; }
.sk-form-panel .sk-seg label:hover { border-color: #bfcbd9; }
.sk-form-panel .sk-seg input:checked + label { border-color: #F5841F; background: #FFF5EB; color: #9a4a06; font-weight: 500; }
.sk-seg input:focus-visible + label { box-shadow: 0 0 0 3px rgba(245,132,31,.25); }
.has-error .sk-seg label { border-color: #e7b3ae; }
.sk-legal { font-size: 12.5px; color: #4A6080; margin: 12px 0 0; text-align: center; }
.sk-legal a, .sk-form-footer a { color: #2069C8; text-decoration: underline; font-weight: 500; }
.sk-legal a:hover, .sk-form-footer a:hover { color: #174f99; }
.sk-sso { margin: 4px 0 0; }
.sk-sso-row { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: 10px; }
.sk-sso-google { width: 100%; height: 52px; display: flex; align-items: center; justify-content: center; gap: 10px; background: #fff; border: 1.5px solid rgba(18,46,82,0.22); border-radius: 11px; color: #0d1f35; font: 500 15px 'Roboto', sans-serif; cursor: pointer; transition: border-color .15s, background .15s; }
.sk-sso-google:hover { border-color: #bfcbd9; background: #FAFCFE; }
.sk-sso-amz { width: 100%; height: 52px; display: flex; align-items: center; justify-content: center; gap: 8px; background: #FFD814; border: 1px solid #F2C200; border-radius: 11px; color: #0F1111; font: 700 16px 'Roboto', sans-serif; cursor: pointer; box-shadow: 0 2px 6px rgba(213,169,0,.30); transition: background .15s; }
.sk-sso-amz:hover { background: #F7CA00; border-color: #E0B400; }
.sk-sso-amz.loading { pointer-events: none; opacity: .75; }
.sk-amz-logo { display: block; height: 24px; width: auto; margin-top: 5px; }
.sk-region-pick { margin-top: 8px; text-align: center; font-size: 13px; }
.sk-link-btn { background: none; border: 0; padding: 0; color: #2069C8; font: 500 13px 'Roboto', sans-serif; cursor: pointer; }
.sk-link-btn:hover { text-decoration: underline; }
.sk-region-select { display: flex; align-items: center; justify-content: center; gap: 10px; color: #4A6080; font-size: 13px; font-weight: 500; }
.sk-region-select .sk-select { width: auto; min-width: 180px; height: 38px; padding: 0 12px; }
.sk-or { display: flex; align-items: center; gap: 12px; color: #8099B8; font-size: 13px; margin: 18px 0; }
.sk-or::before, .sk-or::after { content: ""; flex: 1; height: 1px; background: rgba(18,46,82,0.12); }
.sk-amz-connected { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #16A34A; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 999px; padding: 4px 12px; margin-bottom: 12px; }
.sk-next-note { margin-top: 18px; padding: 14px; background: #F5F8FC; border: 1px solid rgba(18,46,82,0.12); border-radius: 10px; font-size: 13.5px; color: #4A6080; display: flex; gap: 12px; align-items: flex-start; line-height: 1.5; }
.sk-next-note svg { flex: none; margin-top: 1px; }
.sk-next-note b { color: #0d1f35; }
</style>



// WEBPACK FOOTER //
// SignupPage.vue?23ee04f9